import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toPng } from 'html-to-image';
import { toast } from 'sonner';

import { buildChartExportFilename } from '@/components/charts/chart-export.utils';
import { ChartExportFilter } from '@/components/charts/types';
import { triggerBlobDownload } from '@/lib/download';

interface UseChartExportOptions {
  title: string;
  activeTabLabel: string;
  exportFilters?: Array<ChartExportFilter>;
}

const EXPORT_PADDING_PX = 16;

export const useChartExport = (options: UseChartExportOptions) => {
  const { t } = useTranslation();
  const captureRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const exportChart = useCallback(async () => {
    const source = captureRef.current;
    if (!source || isExporting) return;

    setIsExporting(true);

    // Clone off-screen so capturing doesn't flicker the live chart.
    const clone = source.cloneNode(true);
    if (!(clone instanceof HTMLElement)) {
      setIsExporting(false);
      return;
    }

    clone.classList.add('flex', 'flex-col', 'gap-4', 'p-4');

    const exportHeader = clone.querySelector<HTMLElement>(
      '[data-chart-export-header]'
    );
    if (exportHeader) {
      exportHeader.style.display = 'block';
    }

    const container = document.createElement('div');
    container.setAttribute('aria-hidden', 'true');
    Object.assign(container.style, {
      position: 'fixed',
      top: '0',
      left: '-100000px',
      pointerEvents: 'none',
      width: `${source.offsetWidth + EXPORT_PADDING_PX * 2}px`,
    });
    container.appendChild(clone);
    document.body.appendChild(container);

    try {
      // https://developer.mozilla.org/en-US/docs/Web/API/Window/getComputedStyle
      const backgroundColor = getComputedStyle(document.body).backgroundColor;
      const dataUrl = await toPng(clone, {
        pixelRatio: 2,
        backgroundColor,
      });

      const blob = await (await fetch(dataUrl)).blob();
      triggerBlobDownload(
        blob,
        buildChartExportFilename({
          title: options.title,
          tabLabel: options.activeTabLabel,
          exportFilters: options.exportFilters,
        })
      );
    } catch {
      toast.error(t('general.toast.somethingWentWrong'));
    } finally {
      container.remove();
      setIsExporting(false);
    }
  }, [
    options.title,
    options.activeTabLabel,
    options.exportFilters,
    t,
    isExporting,
  ]);

  return { captureRef, exportChart, isExporting };
};
